// usePermissions Hook - RBAC Permissions Management
import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * RBAC Permissions hook for checking user permissions and roles
 *
 * @returns {Object} Permission checking methods and role information
 */
export const usePermissions = () => {
  const { user, isAuthenticated, roles, permissions, maxRoleLevel } = useAuth();

  // Future RBAC state (when implemented)
  // Removed useSelector import as it's not currently used

  // Current user groups/roles from user object (support both legacy groups and new RBAC roles)
  const userGroups = useMemo(() => {
    let groups = [];

    // Legacy groups support
    if (user?.groups && Array.isArray(user.groups)) {
      groups = [...user.groups];
    }

    // New RBAC roles support - prefer roles from auth state
    if (roles && Array.isArray(roles)) {
      groups = [...groups, ...roles];
    } else if (user?.roles && Array.isArray(user.roles)) {
      groups = [...groups, ...user.roles];
    }

    return groups;
  }, [user, roles]);

  const userPermissions = useMemo(() => {
    // Use permissions from auth state if available
    if (permissions && Array.isArray(permissions)) {
      return permissions;
    }

    let perms = [];

    // Legacy user_permissions support
    if (user?.user_permissions && Array.isArray(user.user_permissions)) {
      perms = [...user.user_permissions];
    }

    // New RBAC permissions support
    if (user?.rbac_permissions && Array.isArray(user.rbac_permissions)) {
      perms = [...perms, ...user.rbac_permissions];
    }

    // Extract permissions from roles
    if (user?.roles && Array.isArray(user.roles)) {
      user.roles.forEach((role) => {
        if (role.permissions && Array.isArray(role.permissions)) {
          perms = [...perms, ...role.permissions];
        }
      });
    }

    // Extract permissions from roles in auth state
    if (roles && Array.isArray(roles)) {
      roles.forEach((role) => {
        if (role.permissions && Array.isArray(role.permissions)) {
          perms = [...perms, ...role.permissions];
        }
      });
    }

    // Remove duplicates
    return [...new Set(perms)];
  }, [user, permissions, roles]);

  // Role level mapping (for hierarchical permissions)
  const roleLevels = useMemo(
    () => ({
      'super admin': 100,
      super_admin: 100,
      superadmin: 100,
      admin: 80,
      manager: 60,
      'bureau agent': 50,
      'bureau agents staff': 50,
      supervisor: 40,
      user: 20,
      'client user': 15,
      client: 10,
      guest: 0,
    }),
    []
  );

  // Get user's highest role level
  const getMaxRoleLevel = useCallback(() => {
    if (!isAuthenticated || !userGroups.length) return 0;

    return Math.max(
      ...userGroups.map((group) => {
        const groupName = typeof group === 'string' ? group : group.name;
        return roleLevels[groupName?.toLowerCase()] || 0;
      })
    );
  }, [userGroups, roleLevels, isAuthenticated]);

  // Check if user has specific permission
  const hasPermission = useCallback(
    (permission) => {
      if (!isAuthenticated) return false;
      if (!permission) return true;

      // Super Admin has all permissions - check directly without using isSuperAdmin
      const hasSuperAdminRole = userGroups.some((group) => {
        const groupName = typeof group === 'string' ? group : group.name;
        const roleName = groupName?.toLowerCase();
        return (
          roleName === 'super admin' || roleName === 'super_admin' || roleName === 'superadmin'
        );
      });

      if (hasSuperAdminRole) return true;

      // Check direct permissions
      const hasDirectPermission = userPermissions.some((perm) => {
        const permName = typeof perm === 'string' ? perm : perm.codename || perm.name;
        return permName === permission;
      });

      if (hasDirectPermission) return true;

      // Check group permissions (basic implementation)
      // This will be enhanced when full RBAC is implemented
      return false;
    },
    [userPermissions, isAuthenticated, userGroups]
  );

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback(
    (permissions) => {
      if (!Array.isArray(permissions)) return hasPermission(permissions);
      return permissions.some((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  // Check if user has all specified permissions
  const hasAllPermissions = useCallback(
    (permissions) => {
      if (!Array.isArray(permissions)) return hasPermission(permissions);

      // Super Admin has all permissions - check directly
      const hasSuperAdminRole = userGroups.some((group) => {
        const groupName = typeof group === 'string' ? group : group.name;
        const roleName = groupName?.toLowerCase();
        return (
          roleName === 'super admin' || roleName === 'super_admin' || roleName === 'superadmin'
        );
      });

      if (hasSuperAdminRole) return true;

      return permissions.every((permission) => hasPermission(permission));
    },
    [hasPermission, userGroups]
  );

  // Check if user has specific role/group
  const hasRole = useCallback(
    (role) => {
      if (!isAuthenticated) return false;
      if (!role) return true;

      return userGroups.some((group) => {
        const groupName = typeof group === 'string' ? group : group.name;
        return groupName?.toLowerCase() === role.toLowerCase();
      });
    },
    [userGroups, isAuthenticated]
  );

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback(
    (roles) => {
      if (!Array.isArray(roles)) return hasRole(roles);
      return roles.some((role) => hasRole(role));
    },
    [hasRole]
  );

  // Check if user has minimum role level
  const hasMinRoleLevel = useCallback(
    (minLevel) => {
      const userLevel = getMaxRoleLevel();
      return userLevel >= minLevel;
    },
    [getMaxRoleLevel]
  );

  // Check if user has minimum role by name
  const hasMinRole = useCallback(
    (roleName) => {
      const minLevel = roleLevels[roleName?.toLowerCase()] || 0;
      return hasMinRoleLevel(minLevel);
    },
    [hasMinRoleLevel, roleLevels]
  );

  // Check if user is super admin
  const isSuperAdmin = useCallback(() => {
    // Check if user has superuser flag (Django superuser)
    if (user?.is_superuser) return true;

    // Check maxRoleLevel from auth state (3 = super admin)
    if (maxRoleLevel >= 3) return true;

    // Check traditional role names
    return hasRole('super admin') || hasRole('super_admin') || hasRole('superadmin');
  }, [hasRole, user, maxRoleLevel]);

  // Check if user is admin (admin or above)
  const isAdmin = useCallback(() => {
    return hasMinRole('admin');
  }, [hasMinRole]);

  // Check if user is manager (manager or above)
  const isManager = useCallback(() => {
    return hasMinRole('manager');
  }, [hasMinRole]);

  // Portal access checks - Updated to allow admin access to all portals
  const canAccessPortal = useCallback(
    (portalName) => {
      const normalizedPortal = portalName?.toLowerCase();

      // Super admins can access all portals
      if (isSuperAdmin()) {
        return true;
      }

      // Admin users can access admin, bureau, client, tiplock, sales and insurance portals
      if (user?.assigned_portal === 'admin' || isAdmin() || user?.max_role_level >= 2) {
        const adminAccessiblePortals = [
          'admin',
          'bureau',
          'client',
          'tiplock',
          'sales',
          'insurance',
        ];
        return adminAccessiblePortals.includes(normalizedPortal);
      }

      // Check if user has an assigned portal (primary portal)
      if (user?.assigned_portal) {
        if (user.assigned_portal === normalizedPortal) {
          return true;
        }
      }

      // Check if user has portal in allowed_portals (multi-portal access)
      if (user?.allowed_portals && Array.isArray(user.allowed_portals)) {
        if (user.allowed_portals.includes(normalizedPortal)) {
          return true;
        }
      }

      // Fallback to role-based checks for backward compatibility
      switch (normalizedPortal) {
        case 'super-admin':
        case 'superadmin':
          return isSuperAdmin();
        case 'admin':
          // Check hierarchy level for admin access (level >= 2)
          return user?.max_role_level >= 2 || hasRole('admin') || hasRole('super admin');
        case 'bureau':
          // Bureau portal can be accessed by bureau staff or higher
          return (
            hasAnyRole([
              'bureau agent',
              'bureau agents staff',
              'supervisor',
              'admin',
              'super admin',
            ]) || user?.max_role_level >= 2
          );
        case 'tiplock':
          // TipLock portal can be accessed by tiplock staff or higher
          return (
            hasAnyRole(['tiplock staff', 'tiplock agent', 'admin', 'super admin']) ||
            user?.max_role_level >= 2
          );
        case 'client':
          // Client portal - most permissive, any authenticated user with client assignment
          return hasRole('client user') || hasRole('client') || isAdmin() || isAuthenticated;
        case 'sales':
          // Sales portal - accessible by sales roles or admin+
          return (
            hasAnyRole(['sales rep', 'sales', 'admin', 'super admin']) ||
            user?.max_role_level >= 2
          );
        case 'insurance':
          // Admin-only for now: admins and super-admins were admitted above.
          // Refuse everyone else instead of falling through to the
          // authenticated-means-allowed default below.
          return false;
        default:
          return isAuthenticated;
      }
    },
    [isSuperAdmin, isAdmin, hasAnyRole, hasRole, isAuthenticated, user]
  );

  // Feature access checks
  const canAccessFeature = useCallback(
    (feature) => {
      if (!isAuthenticated) return false;

      // Define feature permissions
      const featurePermissions = {
        fleet_management: ['fleet.view', 'fleet.manage'],
        user_management: ['user.view', 'user.manage'],
        alerts_monitoring: ['alert.view'],
        event_management: ['event.view', 'event.create'],
        suppression_management: ['suppression.view', 'suppression.manage'],
        audit_logs: ['audit.view'],
        system_settings: ['system.manage'],
      };

      const requiredPermissions = featurePermissions[feature];
      if (!requiredPermissions) return true; // Unknown feature, allow access

      return hasAnyPermission(requiredPermissions);
    },
    [isAuthenticated, hasAnyPermission]
  );

  // Get user's effective permissions (includes both direct and group permissions)
  const getEffectivePermissions = useCallback(() => {
    if (!isAuthenticated) return [];

    // Start with direct permissions
    let effectivePermissions = [...userPermissions];

    // Add group-based permissions (basic implementation)
    userGroups.forEach((group) => {
      // const groupName = typeof group === "string" ? group : group.name;
      // This will be enhanced when full RBAC is implemented
      // For now, we'll add basic role-based permissions
    });

    return effectivePermissions;
  }, [userPermissions, userGroups, isAuthenticated]);

  return {
    // State
    userGroups,
    userPermissions,
    maxRoleLevel: getMaxRoleLevel(),

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Role checks
    hasRole,
    hasAnyRole,
    hasMinRole,
    hasMinRoleLevel,

    // Convenience role checks
    isSuperAdmin,
    isAdmin,
    isManager,

    // Access checks
    canAccessPortal,
    canAccessFeature,

    // Utilities
    getEffectivePermissions,
    getMaxRoleLevel,
  };
};

export default usePermissions;
